import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  AspectRatio,
  Box,
  BoxProps,
  chakra,
  Heading,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import { DateTime } from 'luxon';
import Image from 'next/image';
import NextLink from 'next/link';
import React, { useState } from 'react';
import { SongMetadata } from '../lib/types';
import { SONGADAY_CONTRACT_ADDRESS } from '../utils/constants';
import { getSongAttributeValue, youTubeGetID } from '../utils/helpers';
import { parseTokenURI } from '../web3/helpers';
import YoutubeEmbed from './YoutubeEmbed';

const IFrame = chakra('iframe');
interface SongCardProps {
  song: SongMetadata;
}

interface SongTagProps {
  attributes: SongMetadata['attributes'];
  tagsExpanded: boolean;
}

const SongTags: React.FC<SongTagProps> = ({ attributes, tagsExpanded }) => {
  const allowedTraits = [
    'Instrument',
    'Beard',
    'Mood',
    'Location',
    'Genre',
    'Style',
    'Tempo',
    'Key',
  ];
  // For each trait, collect all the attributes
  const tagItems = allowedTraits.reduce((tags, currTrait) => {
    const _allAttributes = attributes
      ?.filter((attribute) => attribute.trait_type === currTrait)
      .map((attr) => attr.value);

    if (_allAttributes.length > 0)
      tags.push(
        `${currTrait}${_allAttributes.length > 1 ? 's' : ''}: ${_allAttributes.join(', ')}`,
      );

    return tags;
  }, []);

  const renderTags = (tags) => {
    const displayMax = 4;
    if (!tagsExpanded) tags = tags.slice(0, displayMax);
    return tags.map((tagContent, index) => {
      return (
        <Tag key={index} size="sm" colorScheme="teal">
          {tagContent}
        </Tag>
      );
    });
  };

  return <Wrap>{renderTags(tagItems)}</Wrap>;
};
function AuctionSongCard({ song }: BoxProps & SongCardProps) {
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const date = DateTime.fromFormat(
    getSongAttributeValue(song?.attributes, 'Date') ?? '',
    'yyyy-MM-dd',
  );

  const subtitleDateString = date.toLocaleString(DateTime.DATE_MED);

  // Get the `tokenId` from the url param if it exists, otherwise try and
  // get it from the generated list of ids.
  const finalTokenId = song?.token_id;

  if (!song?.image) {
    console.log(`Song still not completely indexed by Opensea for song # ${song?.token_id}`);
    return null;
  }

  // const ownedByJonathan = data?.ownerships[0]?.owner?.id === JONATHAN_ID;

  // const { account, provider } = Account.useContainer();
  // const [isBuyLoading, setIsBuyLoading] = useState(false);
  // const [showBuyButton, setShowBuyButton] = useState(false);

  // useEffect(() => {
  //   setShowBuyButton(finalTokenId && openSeaPort && ownedByJonathan);
  // }, [openSeaPort, ownedByJonathan, finalTokenId]);

  // const buyAsset = async () => {
  //   if (openSeaPort && showBuyButton) {
  //     setIsBuyLoading(true);
  //     setIsModalOpen(true);
  //     try {
  //       const order = await openSeaPort?.api.getOrder({
  //         side: 1,
  //         token_id: finalTokenId,
  //         asset_contract_address: ASSET_CONTRACT_ADDRESS,
  //       });
  //       await openSeaPort?.fulfillOrder({ order, accountAddress: address });
  //       toast({
  //         title: 'Transaction Successful!',
  //         description: 'Thank you for your purchase',
  //         status: 'success',
  //         position: 'top',
  //       });
  //       setIsBuyLoading(false);
  //       setIsModalOpen(false);
  //       setShowBuyButton(false);
  //     } catch (error) {
  //       setIsBuyLoading(false);
  //       setIsModalOpen(false);
  //       toast({
  //         title: 'An error has occurred',
  //         description: error.message,
  //         status: 'error',
  //         position: 'top',
  //       });
  //     }
  //   }
  // };

  return (
    <VStack
      {...{
        borderRadius: 'md',
        border: '1px',
        borderColor: 'brand.lightTeal',
        shadow: 'sm',
        _hover: {
          shadow: 'lg',
          transform: 'scale(1.02)',
        },
        transition: 'all 200ms ease-out',
      }}
      spacing="2"
      alignItems="stretch"
      bg="white"
      w="full"
    >
      <Text
        fontSize="sm"
        opacity={0.8}
        fontWeight="semibold"
        textTransform="uppercase"
        p={4}
        alignSelf="start"
        color="teal.600"
      >
        #{song?.token_id} {subtitleDateString ? `— ${subtitleDateString}` : ''}
      </Text>
      <Tabs colorScheme="teal" px={1}>
        <TabList>
          {(song?.youtube_url || song?.animation_url) && <Tab>See video</Tab>}
          {song?.image && <Tab>See illustration</Tab>}
        </TabList>
        <TabPanels>
          <TabPanel>
            <AspectRatio ratio={16 / 9}>
              {song?.youtube_url ? (
                <YoutubeEmbed id={youTubeGetID(song?.youtube_url)} />
              ) : song?.animation_url ? (
                <IFrame title={song?.name} src={song?.animation_url} />
              ) : (
                <>
                  <Text>Loading image...</Text>
                  <Image
                    placeholder="blur"
                    blurDataURL="data:image/webp;base64,UklGRjYEAABXRUJQVlA4WAoAAAAgAAAAEQEAtgAASUNDUBgCAAAAAAIYAAAAAAQwAABtbnRyUkdCIFhZWiAAAAAAAAAAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAAHRyWFlaAAABZAAAABRnWFlaAAABeAAAABRiWFlaAAABjAAAABRyVFJDAAABoAAAAChnVFJDAAABoAAAAChiVFJDAAABoAAAACh3dHB0AAAByAAAABRjcHJ0AAAB3AAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAFgAAAAcAHMAUgBHAEIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFhZWiAAAAAAAABvogAAOPUAAAOQWFlaIAAAAAAAAGKZAAC3hQAAGNpYWVogAAAAAAAAJKAAAA+EAAC2z3BhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABYWVogAAAAAAAA9tYAAQAAAADTLW1sdWMAAAAAAAAAAQAAAAxlblVTAAAAIAAAABwARwBvAG8AZwBsAGUAIABJAG4AYwAuACAAMgAwADEANlZQOCD4AQAA8BcAnQEqEgG3AD7tdLFUuiaqIyJSKstAHYlpbuAEfY/tUYTNud14x+gDF1nLNP/VMZMo00fBbUUUUUjJj7PJ0X+eSsK2BwOBvr9YSlETr8Vut88p48AaseWpym/YbgASiaBeXzm38tUGLTK7tMV3ZPqEK8hS2XSY8EYVMvEm/aTJpLepU2PA7iZujT2ddZM6ijTzBUv6c2uQ5t4W6OAlD3z9rQ1rjrYFZp4hHZhMwKIt3a3/7nNxS4jp2U/IhL+v5uuFT4BdRcAA/tDfxGGxr8GW2zkFsJDasAiiPr7QI+AUmkYrvmljHyGPIFX72qwLl1nZkqoFUUnKd6vr+uD8dC4sSvNgQXKkne2ioD4EKqcrJDGv8uOSdrpPxyDkPSi7SyP/Yq/J5S2JFmo7wSzOAPZnSldFgfRGDEx72hw14BDO81FbuFP9qszXJmMi/me6ch4vUSGCtvzMbNZJ9msM/xzj4p9oUsY6FB1SIX2vLBkmrVAyzvAOhp8PXTkpL/fwUbItTFmAa1MhAme3AEB+hDOJdsneeFyRApteJj8AHiQAADBIpsQeDHiv4oB5OEcTvQWYYGgAAM1sLbboWP1iT+qjtb5VnB3w4MO7N3ClEGlUhn7UIt6Gro6EKVoKJVUQz84aaHPxUroqmDoW4O1wL0RXHCIgAAAA"
                    layout="fill"
                    src={parseTokenURI(song?.image) ?? ''}
                  />
                </>
              )}
            </AspectRatio>
          </TabPanel>
          <TabPanel>
            <AspectRatio ratio={21 / 9}>
              <>
                <Text>Loading image...</Text>
                <Image
                  placeholder="blur"
                  blurDataURL="data:image/webp;base64,UklGRjYEAABXRUJQVlA4WAoAAAAgAAAAEQEAtgAASUNDUBgCAAAAAAIYAAAAAAQwAABtbnRyUkdCIFhZWiAAAAAAAAAAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAAHRyWFlaAAABZAAAABRnWFlaAAABeAAAABRiWFlaAAABjAAAABRyVFJDAAABoAAAAChnVFJDAAABoAAAAChiVFJDAAABoAAAACh3dHB0AAAByAAAABRjcHJ0AAAB3AAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAFgAAAAcAHMAUgBHAEIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFhZWiAAAAAAAABvogAAOPUAAAOQWFlaIAAAAAAAAGKZAAC3hQAAGNpYWVogAAAAAAAAJKAAAA+EAAC2z3BhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABYWVogAAAAAAAA9tYAAQAAAADTLW1sdWMAAAAAAAAAAQAAAAxlblVTAAAAIAAAABwARwBvAG8AZwBsAGUAIABJAG4AYwAuACAAMgAwADEANlZQOCD4AQAA8BcAnQEqEgG3AD7tdLFUuiaqIyJSKstAHYlpbuAEfY/tUYTNud14x+gDF1nLNP/VMZMo00fBbUUUUUjJj7PJ0X+eSsK2BwOBvr9YSlETr8Vut88p48AaseWpym/YbgASiaBeXzm38tUGLTK7tMV3ZPqEK8hS2XSY8EYVMvEm/aTJpLepU2PA7iZujT2ddZM6ijTzBUv6c2uQ5t4W6OAlD3z9rQ1rjrYFZp4hHZhMwKIt3a3/7nNxS4jp2U/IhL+v5uuFT4BdRcAA/tDfxGGxr8GW2zkFsJDasAiiPr7QI+AUmkYrvmljHyGPIFX72qwLl1nZkqoFUUnKd6vr+uD8dC4sSvNgQXKkne2ioD4EKqcrJDGv8uOSdrpPxyDkPSi7SyP/Yq/J5S2JFmo7wSzOAPZnSldFgfRGDEx72hw14BDO81FbuFP9qszXJmMi/me6ch4vUSGCtvzMbNZJ9msM/xzj4p9oUsY6FB1SIX2vLBkmrVAyzvAOhp8PXTkpL/fwUbItTFmAa1MhAme3AEB+hDOJdsneeFyRApteJj8AHiQAADBIpsQeDHiv4oB5OEcTvQWYYGgAAM1sLbboWP1iT+qjtb5VnB3w4MO7N3ClEGlUhn7UIt6Gro6EKVoKJVUQz84aaHPxUroqmDoW4O1wL0RXHCIgAAAA"
                  layout="fill"
                  src={parseTokenURI(song?.image) ?? ''}
                />
              </>
            </AspectRatio>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <VStack spacing={4} px={6} pb={4} align="stretch">
        <Box>
          <Heading as="h3" fontSize="2xl" noOfLines={3}>
            {song?.name}
          </Heading>
        </Box>
        <hr />
        <Text fontSize="md" noOfLines={3}>
          {song?.description}
        </Text>

        {/* <Wrap> */}
        {song?.attributes && (
          <Box>
            <SongTags attributes={song.attributes} tagsExpanded={tagsExpanded} />

            <Link fontSize="small" onClick={() => setTagsExpanded(!tagsExpanded)}>
              {tagsExpanded ? 'Show fewer tags' : 'Show all tags'}
              {tagsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Link>
          </Box>
        )}

        <NextLink
          href={`https://opensea.io/assets/${SONGADAY_CONTRACT_ADDRESS}/${finalTokenId}`}
          passHref
        >
          <Link
            isExternal
            fontSize="sm"
            fontWeight="medium"
            textDecoration="underline"
            _hover={{
              textDecoration: 'none',
            }}
          >
            View on OpenSea
          </Link>
        </NextLink>
      </VStack>

      {/* margin collapse fix */}
      <Box />
    </VStack>
  );
}

export default AuctionSongCard;
